import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Leaderboard from "@/components/Leaderboard";
import EquityChart from "@/components/EquityChart";
import TraderCard from "@/components/TraderCard";
import TradeFeed from "@/components/TradeFeed";
import StockWatchlist from "@/components/StockWatchlist";
import ShareDialog from "@/components/ShareDialog";
import RecapPanel from "@/components/RecapPanel";
import {
  fetchCompetition,
  fetchTraders,
  fetchTrades,
  fetchSnapshots,
  fetchStocks,
  fetchRecaps,
  runRound,
  resetCompetition,
} from "@/lib/api";

export default function Dashboard({ spectator = false }) {
  const [competition, setCompetition] = useState(null);
  const [traders, setTraders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [snapshots, setSnapshots] = useState({});
  const [stocks, setStocks] = useState([]);
  const [recaps, setRecaps] = useState([]);
  const [running, setRunning] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const refreshAll = useCallback(async () => {
    try {
      const [c, t, tr, sn, st, rc] = await Promise.all([
        fetchCompetition(),
        fetchTraders(),
        fetchTrades(40),
        fetchSnapshots(),
        fetchStocks(),
        fetchRecaps(5),
      ]);
      setCompetition(c);
      setTraders(t);
      setTrades(tr);
      setSnapshots(sn);
      setStocks(st);
      setRecaps(rc);
    } catch (e) {
      console.error("refresh failed", e);
    }
  }, []);

  useEffect(() => {
    refreshAll();
    const id = setInterval(refreshAll, 15000);
    return () => clearInterval(id);
  }, [refreshAll]);

  const handleRun = async () => {
    if (running || spectator) return;
    setRunning(true);
    toast.info("Asking the AIs for their next move…");
    try {
      await runRound();
      toast.success("Round complete — board updated.");
      await refreshAll();
    } catch (e) {
      console.error(e);
      toast.error("Round failed. Check backend logs.");
    } finally {
      setRunning(false);
    }
  };

  const handleReset = async () => {
    if (spectator) return;
    if (!window.confirm("Reset the entire 7-day competition?")) return;
    setRunning(true);
    try {
      await resetCompetition();
      toast.success("Competition reset. Round triggered.");
      await refreshAll();
    } catch (e) {
      console.error(e);
      toast.error("Reset failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header
        competition={competition}
        onRun={handleRun}
        onReset={handleReset}
        onShare={() => setShareOpen(true)}
        running={running}
        spectator={spectator}
      />

      {stocks.length > 0 && (
        <div className="border-b border-arena-line bg-arena-alt/40 overflow-hidden">
          <div className="ticker-track flex gap-10 py-2 px-6">
            {[...stocks, ...stocks].map((s, i) => (
              <span key={i} className="font-mono text-xs tabular-nums whitespace-nowrap">
                <span className="font-bold">{s.symbol}</span>
                <span className="text-arena-soft"> ${s.price.toFixed(2)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-px bg-arena-line">
        <div className="bg-white">
          <Leaderboard traders={traders} />
        </div>

        <div className="bg-white">
          <RecapPanel recaps={recaps} onRefresh={refreshAll} spectator={spectator} />
        </div>

        <div className="bg-white">
          <EquityChart snapshots={snapshots} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 arena-grid">
          {traders.map((t) => (
            <TraderCard key={t.id} trader={t} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 arena-grid">
          <div className="lg:col-span-2 bg-white">
            <TradeFeed trades={trades} />
          </div>
          <div className="bg-white">
            <StockWatchlist stocks={stocks} />
          </div>
        </div>

        <div className="bg-white p-6 lg:p-8 flex items-center justify-between flex-wrap gap-4 border border-arena-line">
          <div>
            <div className="font-display font-black text-lg tracking-tight">How it works</div>
            <p className="text-xs text-arena-soft mt-1 max-w-2xl">
              Every {Math.round((competition?.interval_minutes || 240) / 60)} hours, each AI receives the
              same portfolio state and market data and replies with a JSON trade decision — stocks or
              options. We execute it, record a snapshot, and rank them by total portfolio value over 7
              days. No human intervention.
            </p>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
            Live quotes via Yahoo Finance · Emergent LLM
          </div>
        </div>
      </main>

      <footer className="border-t border-arena-line py-6 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
        AI Trading Arena · Built on Emergent
      </footer>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} traders={traders} />
    </div>
  );
}
